// AdminSlatesNewPage.tsx — (TF-6b-3; design §3, 00:00 answer 2) New slate: Generate (seeds, a prompt, how many, your own
// ideas) or Develop (your ideas, each kept and filled out), the audience, the main platform. Transform and Variants are
// shown greyed ("later"), Recreate greyed ("comes with C1.7b").
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer, TfCard, Btn } from "@/components/v2/primitives.tsx";
import { engineSlatesApi } from "@/api/engine-slates.ts";
import { rememberSeries } from "@/lib/series.ts";
import { isChild, CHILD_LABEL } from "@/lib/audiences.ts";   // (tf3-42) an audience of children: disabled, in words
import { slateRefusalText, SLATE_OPERATION_CHOICES, SLATE_HISTORY_KEY, slateAudienceOk, audienceFirstOffered } from "@/lib/slates.ts";

const storage = () => { try { return window.localStorage; } catch { return null; } };   // catch-reason: no storage → no history
const lines = (t: string) => t.split("\n").map((x) => x.trim()).filter(Boolean);

export function AdminSlatesNewPage() {
  const nav = useNavigate();
  const [audiences, setAudiences] = useState<{ id: string; label: string; resolvedAgeBand?: string | null }[] | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [operation, setOperation] = useState<"generate" | "develop">("generate");
  const [audienceId, setAudience] = useState("");
  const [primaryPlatform, setPlatform] = useState<"tiktok" | "youtube">("tiktok");
  const [count, setCount] = useState("10");
  const [prompt, setPrompt] = useState("");
  const [seeds, setSeeds] = useState("");
  const [ideas, setIdeas] = useState("");
  const [audienceFirst, setAudienceFirst] = useState(false);   // (tf3-40; C2.1-1) interests → pairs → ideas, each reviewed
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    engineSlatesApi.audiences().then((r) => setAudiences(r.profiles))
      .catch((e) => setReadErr(`the audience profiles couldn't be read: ${slateRefusalText(e)}`));   // catch-reason: shown in place of the form
  }, []);
  const typed = lines(ideas);
  const firstOffered = audienceFirstOffered(operation, audienceId, typed.length);
  const first = audienceFirst && firstOffered;   // unticked, or no longer offered: today's slate
  const ready = first || (operation === "develop" ? typed.length > 0 : Number(count) >= 1 && Number(count) <= 20);
  const create = async () => {
    setBusy(true); setErr(null);
    try {
      // (tf3-40) audience first: contracts' request is Generate, the audience, no count and no typed ideas
      const r = await engineSlatesApi.create(first ? { operation: "generate", primaryPlatform, audienceId, audienceFirst: true } : { operation, primaryPlatform, audienceId: audienceId || null,
        ...(operation === "generate" ? { count: Number(count), prompt: prompt.trim() || null, seeds: seeds.split(",").map((x) => x.trim()).filter(Boolean) } : {}),
        ideas: typed.map((text) => ({ text })) });
      rememberSeries(storage(), { id: r.slateId, title: r.slateId }, new Date(), SLATE_HISTORY_KEY);
      nav(`/admin/slates/${r.slateId}`);
    } catch (e) { setErr(slateRefusalText(e)); }   // catch-reason: shown under the button; nothing was made
    finally { setBusy(false); }
  };
  if (readErr) return <PageContainer title="New slate"><div style={{ color: "#b91c1c" }}>{readErr}</div></PageContainer>;
  if (!audiences) return <PageContainer title="New slate"><div className="tf-muted">Reading…</div></PageContainer>;
  const field = { display: "block", width: "100%", marginTop: 4, padding: 8 } as const;
  return (
    <PageContainer title="New slate" subtitle="One text call writes the cards (usually under a minute)." maxWidth={720}>
      <TfCard>
        <label>What to make
          <select style={field} value={operation} onChange={(e) => setOperation(e.target.value as "generate" | "develop")}>
            {SLATE_OPERATION_CHOICES.map((o) => <option key={o.op} value={o.op} disabled={!o.enabled}>{o.label}{o.note ? ` (${o.note})` : ""}</option>)}
          </select></label>
        <label style={{ display: "block", marginTop: 12 }}>Audience
          <select style={field} value={audienceId} onChange={(e) => setAudience(e.target.value)}>
            <option value="">None</option>
            {audiences.map((a) => <option key={a.id} value={a.id} disabled={!slateAudienceOk(a.id) || isChild(a.resolvedAgeBand)}>{a.label}{isChild(a.resolvedAgeBand) ? ` (${CHILD_LABEL})` : slateAudienceOk(a.id) ? "" : " (its id has capitals or hyphens: a slate can't use it)"}</option>)}
          </select></label>
        <label style={{ display: "block", marginTop: 12 }}>Main platform
          <select style={field} value={primaryPlatform} onChange={(e) => setPlatform(e.target.value as "tiktok" | "youtube")}>
            <option value="tiktok">TikTok</option><option value="youtube">YouTube</option></select></label>
        {firstOffered && <label style={{ display: "block", marginTop: 12 }}>
          <input type="checkbox" checked={audienceFirst} onChange={(e) => setAudienceFirst(e.target.checked)} /> Audience first: you choose its interests, then the
          interest and format pairs, before the ideas are written</label>}
        {operation === "generate" && !first && <>
          <label style={{ display: "block", marginTop: 12 }}>How many ideas (1–20)<input style={field} value={count} onChange={(e) => setCount(e.target.value)} /></label>
          <label style={{ display: "block", marginTop: 12 }}>Seeds (comma-separated, up to 10)<input style={field} value={seeds} onChange={(e) => setSeeds(e.target.value)} /></label>
          <label style={{ display: "block", marginTop: 12 }}>Prompt (optional)<textarea style={field} rows={2} maxLength={600} value={prompt} onChange={(e) => setPrompt(e.target.value)} /></label>
        </>}
        {!first && <label style={{ display: "block", marginTop: 12 }}>{operation === "develop" ? "Your ideas (one per line, up to 10)" : "Your own ideas too (optional, one per line)"}
          <textarea style={field} rows={4} value={ideas} onChange={(e) => setIdeas(e.target.value)} /></label>}
        <div style={{ marginTop: 16 }}><Btn onClick={create} disabled={!ready || busy}>Write the ideas</Btn></div>
        {err && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 8 }}>{err}</div>}
      </TfCard>
    </PageContainer>
  );
}
